import * as Scenes from "oc-threejs/Scenes";
import * as Debugs from "oc-threejs/Debugs";
import { Jaco3d } from "./Jaco3d";
import * as Commons from "oc-commons/Commons";

import type { Model } from "../arrangement/Model";
import { Box3, Group, Material, Mesh, MeshBasicMaterial, Object3D } from "three";

export class Model3d extends Jaco3d {
  isModel3d = true;

  /** @type {?THREE.Group} */
  group: Group | null = null;
  /** @type {?THREE.Group} */
  originalGroup: Group | null = null;
  changeId: number = Math.random();
  /** @type {?THREE.Mesh} */
  _clippingMesh: Mesh | undefined | null;
  _update3dCounter: number = 0;

  // Debug info container
  _debugInfo: Group | null = null;

  // Narrowing jaco type
  declare jaco: Model;

  constructor(jaco: Model) {
    super(jaco);
  }

  static getClippingMaterial = Commons.cacheIdentity(
    () =>
      new MeshBasicMaterial({
        // für Debug-Zwecke ist eine Anzeige praktisch
        color: 0x000000,
        wireframe: true,
        transparent: true,
        opacity: 0.5,
      }),
  );

  /** */
  update3d() {
    super.update3d();
    // Vorteil, das Clipping direkt im Model3d zu machen: das Model verändert seine Matrix nicht.
    // Daher sind die einzigen Events, die ein Re-Clipping erfordern, Model-Load und Clipping-Parameters
    if (this.changed("clippingParameters") || this.changed("clipping")) {
      this.updateClippingMesh();
      if (!this.changed("key")) {
        this.clipModel();
      }
    }
    // Using simple property access for changed path logic assuming logic is:
    // changed(path, accessor)
    if (this.changed("Model", (that) => (that as Model).modelMaterialRelation.Model.Key)) {
      if (this.group != null) {
        Scenes.remove(this.group);
      }
      this.originalGroup = this.group = this.loadObject3DByModelMaterialRelation(
        this.jaco.modelMaterialRelation,
        () => this.onLoadModel(),
      );
      this.group.name = this.jaco.key;
      this.add(this.group);
      this.dirty();
    } else if (
      this.changed("modelMaterialRelation", (that) => (that as Model).modelMaterialRelation.id)
    ) {
      this.updateMaterialMap();
    }
  }

  /** */
  updateMaterialMap() {
    const meta = this.jaco.getMeta();
    const materialsFactory = this.getMaterialFactory(meta);
    const swap = (m: Material) => {
      const materialNameToSwap = m.userData.originalName ?? m.name;
      return materialsFactory.create(materialNameToSwap);
    };
    Scenes.filter(this.group!, (o) => (o as Mesh).material != null).forEach((o) => {
      const mesh = o as Mesh;
      if (Array.isArray(mesh.material)) {
        mesh.material = mesh.material.map(swap);
      } else {
        mesh.material = swap(mesh.material as Material);
      }
    });
  }

  /** */
  onLoadModel() {
    this.updateClippingMesh();
    this.clipModel();
    this.dirty();
  }

  isEqualClippingParameters() {
    return (
      this.jaco.clippingParameters == null ||
      Commons.isEqualEpsilon(this.jaco.defaultClippingParameters, this.jaco.clippingParameters)
    );
  }

  /** */
  updateClippingMesh() {
    if (this.isEqualClippingParameters()) {
      return;
    }
    if (this._clippingMesh != null) {
      Scenes.remove(this._clippingMesh);
    }
    const geometry = this.jaco.getClippingGeometry();
    const material = Model3d.getClippingMaterial();
    if (geometry) {
      this._clippingMesh = new Mesh(geometry, material);

      // für Debug-Zwecke visible auf true setzen
      this._clippingMesh.visible = false;

      this.add(this._clippingMesh);
    }
  }

  /** */
  clipModel() {
    if (this.isEqualClippingParameters()) {
      return;
    }
    if (this.group != null) {
      Scenes.remove(this.group);
    }
    if (this.originalGroup) {
      this.group = this.originalGroup.clone();
      this.add(this.group);
      this.group.updateMatrixWorld(true);
    }

    if (this.jaco.isClipping() && this.group && this._clippingMesh) {
      // Assuming clippingFunction is defined on Model and callable
      this.jaco.clippingFunction(this.group, this._clippingMesh);
    }

    if (STAGE.settings.showDebugObjects) {
      if (this._debugInfo != null) {
        Scenes.remove(this._debugInfo);
      }
      this._debugInfo = new Group();
      this._debugInfo.add(Debugs.box(this.jaco.getBox(), 0xff0000, true));
      if (this._clippingMesh) {
        const clippingClone = this._clippingMesh.clone();
        clippingClone.visible = true;
        this._debugInfo.add(clippingClone);
      }
      if (this.group) {
        this.group.add(this._debugInfo);
      }
    }

    this.dirty();
  }

  /** */
  dirty() {
    this.changeId++;
    STAGE.dirty();
  }
}

/**
 * Spiegelt eine BufferGeometry entlang einer Achse und korrigiert die Winding Order.
 * @param {THREE.BufferGeometry} geometry - Die zu spiegelnde Geometrie.
 * @param {string} axis - Die Achse: 'x', 'y' oder 'z'.
 */
function mirrorIndexedGeometry(geometry: any, axis: string) {
  // 1. Sicherstellen, dass es eine Indexed Geometry ist
  if (!geometry.index) {
    console.warn(
      "Geometrie hat keinen Index. Konvertierung nötig oder normale Spiegelung verwenden.",
    );
    // Optional: geometry = require('three/examples/jsm/utils/BufferGeometryUtils').mergeVertices(geometry);
    return;
  }

  // 2. Die Geometrie physisch spiegeln (Vertices und Normalen werden hier angepasst)
  switch (axis) {
    case "x":
      geometry.scale(-1, 1, 1);
      break;
    case "y":
      geometry.scale(1, -1, 1);
      break;
    case "z":
      geometry.scale(1, 1, -1);
      break;
    default:
      console.error("Ungültige Achse. Bitte 'x', 'y' oder 'z' verwenden.");
      return;
  }

  // 3. Winding Order korrigieren (Indices flippen)
  // Ein Dreieck besteht aus Indices [a, b, c].
  // Durch die Spiegelung ist es nun "falsch herum".
  // Wir ändern es zu [a, c, b], um die Normale wieder nach außen zeigen zu lassen.

  const index = geometry.index;
  const array = index.array;

  for (let i = 0; i < array.length; i += 3) {
    // Wir tauschen den zweiten und dritten Index jedes Dreiecks
    const temp = array[i + 1];
    array[i + 1] = array[i + 2];
    array[i + 2] = temp;
  }

  // Update an die GPU senden
  index.needsUpdate = true;

  // 4. Normalen neu berechnen (optional, aber empfohlen bei komplexen Spiegelungen)
  // geometry.scale() passt Normalen oft an, aber manchmal ist ein Recompute sauberer,
  // um Lighting-Artefakte zu vermeiden.
  geometry.computeVertexNormals();

  // Bounding Box/Sphere updaten, damit Frustum Culling korrekt funktioniert
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}
